<template>
  <div v-show="isActive" ref="root" class="tab-item h-full">
    <slot />
  </div>
</template>

<script>
import { uuid, computedInstance } from '../../utils/helpers'

import { defineComponent, ref, onMounted, onBeforeUnmount, inject } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-tab-item label="Tab Label">
   *    Content
   * </ak-tab-item>
   */
  name: 'AkTabItem',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Tab label
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String}
     * @description Icon name
     */
    icon: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Item is disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Item is visible
     */
    visible: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @description Sets up the component and returns an object containing
   * all of the attributes for the component to use.
   */
  setup(props, context) {
    const parent = inject('akTabs')
    const root = ref(null)
    const isActive = ref(false)
    const isTabItem = ref(true)
    const uid = uuid()

    /**
     * @type {Function}
     * @description A simple method for setting the tab item as
     * being active
     */
    const activate = () => {
      if (!props.disabled) {
        isActive.value = true
      }
    }

    /**
     * @type {Function}
     * @description A simple method for setting the tab item as
     * not being active
     */
    const deactivate = () => {
      if (!props.disabled) {
        isActive.value = false
      }
    }

    /**
     * @type {Object}
     * @description This is the AkTab instance, we will return this as the component,
     * but we are also going to use it to create a new extended object to pass into
     * the parent component
     */
    const inst = {
      root,
      isActive,
      activate,
      deactivate,
      isTabItem,
      uid,
    }

    onMounted(() => {
      if (parent && typeof parent.registerTab === 'function') {
        const computedInst = computedInstance(props, inst)
        computedInst.slots = context.slots
        parent.registerTab(computedInst)
      }
    })

    onBeforeUnmount(() => {
      if (parent && typeof parent.unregisterTab === 'function') {
        parent.unregisterTab(inst.uid)
      }
    })

    return inst
  },
})
</script>
